// Waits and handling times in bands (internal/report/bands.go): the lines a
// histogram draws, the query that asks for them, and the picker's presets.

import type { StaffKey, Vars } from '../lib/staffText'
import type { Bands, BandSpec } from '../lib/types'

export type SayFn = (k: StaffKey, vars?: Vars) => string
export type BandKind = 'wait' | 'handling'

/** One bar: `share` of all measured, `within` cumulative to this band's edge;
 *  null where nothing was measured (a dash) or the band has no upper edge. */
export type BandLine = { label: string; count: number; share: number | null; within: number | null; over: boolean; notCalled?: true }

export const DefaultBands: BandSpec = { waitBy: 'minutes', waitEdges: [3, 5, 10, 15], handlingEdges: [2, 5, 10, 20] }

const RANGE = { minutes: [1, 240], target: [10, 500] } as const

const rising = (edges: unknown, by: 'minutes' | 'target'): edges is number[] => {
  const [lo, hi] = RANGE[by]
  return (
    Array.isArray(edges) &&
    edges.length >= 1 &&
    edges.length <= 6 &&
    edges.every((e, i) => Number.isInteger(e) && e >= lo && e <= hi && (i === 0 || e > edges[i - 1]))
  )
}

/** "3, 5 10" → [3, 5, 10]; null unless 1–6 rising whole numbers in the unit's range. */
export function parseEdges(text: string, by: 'minutes' | 'target'): number[] | null {
  const parts = text.split(/[\s,]+/).filter(Boolean)
  if (!parts.every((p) => /^\d{1,4}$/.test(p))) return null
  const edges = parts.map(Number)
  return rising(edges, by) ? edges : null
}

/** A spec read back from storage, or null when the server would refuse it. */
export function validBands(x: unknown): BandSpec | null {
  if (!x || typeof x !== 'object') return null
  const s = x as BandSpec
  if (s.waitBy !== 'minutes' && s.waitBy !== 'target') return null
  if (!rising(s.waitEdges, s.waitBy) || !rising(s.handlingEdges, 'minutes')) return null
  return { waitBy: s.waitBy, waitEdges: [...s.waitEdges], handlingEdges: [...s.handlingEdges] }
}

export function bandQuery(spec: BandSpec): string {
  return `waitBy=${spec.waitBy}&waitEdges=${spec.waitEdges.join(',')}&handlingEdges=${spec.handlingEdges.join(',')}`
}

export const BAND_PRESETS: ReadonlyArray<{ id: string; key: StaffKey; spec: Partial<BandSpec> }> = [
  { id: 'min3', key: 'insight.band.p.min3', spec: { waitBy: 'minutes', waitEdges: [3, 5, 10, 15] } },
  { id: 'min1', key: 'insight.band.p.min1', spec: { waitBy: 'minutes', waitEdges: [1, 2, 5, 10] } },
  { id: 'target', key: 'insight.band.p.target', spec: { waitBy: 'target', waitEdges: [50, 100, 150, 200] } },
  { id: 'took2', key: 'insight.band.p.took2', spec: { handlingEdges: [2, 5, 10, 20] } },
  { id: 'took1', key: 'insight.band.p.min1', spec: { handlingEdges: [1, 2, 5, 10] } },
]

/** The presets a tab offers: waiting presets set waitEdges, handling ones handlingEdges. */
export const presetsFor = (kind: BandKind) => BAND_PRESETS.filter((p) => (kind === 'wait' ? p.spec.waitEdges : p.spec.handlingEdges))

const same = (a: number[] | undefined, b: number[]) => !!a && a.length === b.length && a.every((x, i) => x === b[i])

export function presetOn(spec: BandSpec, p: (typeof BAND_PRESETS)[number]): boolean {
  if (p.spec.waitEdges) return p.spec.waitBy === spec.waitBy && same(p.spec.waitEdges, spec.waitEdges)
  return same(p.spec.handlingEdges, spec.handlingEdges)
}

// 150 → "1½", 200 → "2", 50 → "½": a multiple of half the target.
const times = (pct: number) => `${pct >= 100 ? Math.floor(pct / 100) : ''}${pct % 100 === 50 ? '½' : ''}`
// Target edges read as multiples ("1½× target") only when every one is a half.
const halves = (edges: number[]) => edges.every((e) => e % 50 === 0)

/** "3 · 5 · 10 · 15 min"; of the target "½ · 1 · 1½ · 2 × target" or "80 · 100% of target". */
export function edgesText(by: 'minutes' | 'target', edges: number[], say: SayFn): string {
  if (by === 'minutes') return `${edges.join(' · ')} ${say('insight.minutes')}`
  return halves(edges) ? say('insight.band.edgesTimes', { list: edges.map(times).join(' · ') }) : say('insight.band.edgesPct', { list: edges.join(' · ') })
}

/** The short name on the picker's button: the set's kind, or Own for edges no preset has. */
export function bandSetKey(spec: BandSpec, kind: BandKind): StaffKey {
  if (!presetsFor(kind).some((p) => presetOn(spec, p))) return 'insight.band.set.own'
  return kind === 'wait' && spec.waitBy === 'target' ? 'insight.band.set.target' : 'insight.band.set.minutes'
}

/** The tabs the drawn row offers: a kind only where the row carries its bands. */
export function bandTabs(row: { waitBands?: Bands; handlingBands?: Bands } | null | undefined, waitApplies: boolean): BandKind[] {
  return [...(waitApplies && row?.waitBands ? ['wait' as const] : []), ...(row?.handlingBands ? ['handling' as const] : [])]
}

function targetLabel(edges: number[], i: number, say: SayFn): string {
  const x = halves(edges)
  const n = edges.length
  if (i === 0) return x ? say('insight.band.upToTimes', { x: times(edges[0]!) }) : say('insight.band.upToPct', { n: edges[0]! })
  if (i === n) return x ? say('insight.band.overTimes', { x: times(edges[n - 1]!) }) : say('insight.band.overPct', { n: edges[n - 1]! })
  const [a, b] = [edges[i - 1]!, edges[i]!]
  return x ? say('insight.band.betweenTimes', { a: times(a), b: times(b) }) : say('insight.band.betweenPct', { a, b })
}

function minuteLabel(edges: number[], i: number, say: SayFn): string {
  if (i === 0) return say('insight.band.upTo', { n: edges[0]! })
  if (i < edges.length) return say('insight.band.between', { a: edges[i - 1]!, b: edges[i]! })
  return say('insight.band.over', { n: edges[edges.length - 1]! })
}

/** One band named from its own edges (lo 0 = from nothing, hi 0 = open), as a chip says it. */
export function windowLabel(by: 'minutes' | 'target', lo: number, hi: number, say: SayFn): string {
  if (by === 'minutes') {
    if (lo === 0) return say('insight.band.upTo', { n: hi })
    if (hi === 0) return say('insight.band.over', { n: lo })
    return say('insight.band.between', { a: lo, b: hi })
  }
  return targetLabel([lo, hi].filter((e) => e > 0), lo === 0 ? 0 : 1, say)
}

/**
 * The bars of one histogram. Left before call (waits only) is in every share's
 * denominator and is a bar of its own when there is any; with nothing measured
 * every share is null, never NaN.
 */
export function bandRows(b: Bands, spec: BandSpec, kind: BandKind, say: SayFn): BandLine[] {
  const target = kind === 'wait' && spec.waitBy === 'target'
  const edges = kind === 'wait' ? spec.waitEdges : spec.handlingEdges
  const counts = Array.from({ length: edges.length + 1 }, (_, i) => b.counts[i] ?? 0)
  const left = kind === 'wait' ? b.notCalled : 0
  const of = counts.reduce((s, c) => s + c, 0) + left
  const part = (n: number) => (of > 0 ? n / of : null)
  let sum = 0
  const lines: BandLine[] = counts.map((count, i) => {
    sum += count
    return {
      label: target ? targetLabel(edges, i, say) : minuteLabel(edges, i, say),
      count,
      share: part(count),
      within: i < edges.length ? part(sum) : null,
      over: target && (i === 0 ? 0 : edges[i - 1]!) >= 100,
    }
  })
  if (left > 0) lines.push({ label: say('insight.band.left'), count: left, share: part(left), within: null, over: false, notCalled: true })
  return lines
}

/** One edge as the Called within line names it: "3 min"; of the target, the first
 *  says so ("½× target", "80% of target") and the rest are bare ("1×", "120%"). */
function edgeText(by: 'minutes' | 'target', edges: number[], i: number, say: SayFn): string {
  const e = edges[i]!
  if (by === 'minutes') return `${e}\u00a0${say('insight.minutes')}`
  if (halves(edges)) return i === 0 ? say('insight.band.timesTarget', { x: times(e) }) : `${times(e)}×`
  return i === 0 ? say('insight.band.edgesPct', { list: e }) : `${e}%`
}

/** The line above the bars: "Called within" and each edge beside its share,
 *  "3 min 52% · 5 min 70% · 10 min 88% · 15 min 95%". Null with nothing measured. */
export function withinLine(lines: BandLine[], spec: BandSpec, kind: BandKind, say: SayFn): { label: string; value: string } | null {
  const by = kind === 'wait' ? spec.waitBy : 'minutes'
  const edges = kind === 'wait' ? spec.waitEdges : spec.handlingEdges
  const pairs = lines.flatMap((l, i) =>
    l.within === null || edges[i] === undefined
      ? []
      : [
          // A percent edge beside a percent share needs a mark between them.
          say(by === 'target' && !halves(edges) ? 'insight.band.pairTo' : 'insight.band.pair', {
            edge: edgeText(by, edges, i, say),
            share: `${Math.round(l.within * 100)}%`,
          }),
        ],
  )
  if (pairs.length === 0) return null
  return {
    label: say(kind === 'wait' ? 'insight.band.calledWithin' : 'insight.band.servedWithin'),
    value: pairs.join(' · '),
  }
}
